import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    input,
    OnDestroy,
    Output,
    output,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import {
    AudioRecordingEvent,
    AudioRecordingService,
    AudioTranscription,
} from 'Angular/services/audio-recording.service';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { IconModule } from '../../directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';

/**
 * AudioTranscriptionInputComponent component
 *
 * This component displays a text input with controls to start an audio transcription session. During transcription
 * it will display a canvas with mic activity (voice volume) and when done, it will send the audio to
 * AITranscription Django view to generate a transcription. The transcription will be displayed in the input.
 */
@Component({
    standalone: true,
    selector: 'ycn-audio-transcription-input',
    templateUrl: './audio-transcription-input.template.html',
    imports: [IconModule, FormsModule, TooltipModule],
})
export class AudioTranscriptionInputComponent implements OnDestroy {
    @Output() transcribedText = new EventEmitter<string>();

    @Output() transcriptionError = new EventEmitter<string>();

    transcriptionCancel = output<void>();

    transcriptionStop = output<void>();

    @Input() placeholder = '';

    @Input() inputText: string = '';

    protected readonly maxCanvasWidth = input(468);

    // Canvas reference
    @ViewChild('voiceCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;

    // Flags
    protected transcribing: boolean = false;

    protected processing: boolean = false;

    protected settingUpTranscription: boolean = false;

    // Voice visualization properties
    private canvas?: HTMLCanvasElement;

    private canvasCtx?: CanvasRenderingContext2D;

    private voiceActivityHistory: number[] = [];

    private maxHistory = 468;

    private animationId?: number;

    protected currentVolume = 0;

    private strokeWidth = 1.5; // Width of each stroke

    private strokeSpacing = 1.5; // White space between strokes

    private strokeColor = '#0282e8';

    private canvasWidth = 0;

    private canvasHeight = 0;

    // Subscriptions
    private audioRecordingSubscription?: Subscription;

    constructor(
        private cdRef: ChangeDetectorRef,
        private urlsService: UrlsService,
        private ajaxService: AjaxService,
        private audioRecordingService: AudioRecordingService
    ) {}

    ngOnDestroy(): void {
        if (this.audioRecordingSubscription) {
            this.audioRecordingSubscription.unsubscribe();
        }
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    }

    /**
     * Set up the canvas size where the voice activity is going to be displayed
     * @private
     */
    private setupCanvas(): void {
        this.canvas = this.canvasRef.nativeElement;
        this.canvasCtx = this.canvas.getContext('2d') || undefined;

        if (this.canvas && this.canvasCtx) {
            // Set canvas size
            const rect = this.canvas.getBoundingClientRect();

            // Every screen has different physical pixel numbers per CSS pixels. So the size of the canvas
            // depends on the device screen settings.
            const devicePixelRatio = window.devicePixelRatio || 1;

            this.canvas.width = rect.width * devicePixelRatio;
            this.canvas.height = rect.height * devicePixelRatio;
            this.canvasCtx.scale(devicePixelRatio, devicePixelRatio);

            this.canvas.style.width = `${rect.width}px`;
            this.canvas.style.height = `${rect.height}px`;

            this.canvasWidth = rect.width;
            this.canvasHeight = rect.height;

            this.maxHistory = Math.floor(rect.width / (this.strokeWidth + this.strokeSpacing));
        }
    }

    /**
     * Starts the voice activity visualization. It will create the canvas, add dots and then start the animation,
     * which will depend on the history of voice activites recored.
     * @private
     */
    private startVisualization(): void {
        if (this.canvasRef) {
            this.setupCanvas();
        }
        if (!this.canvas || !this.canvasCtx) return;

        this.voiceActivityHistory = [];

        // Clear canvas with white background initially
        this.clearCanvas();
        this.animate();
    }

    /**
     * Stops the canvas animation and clears the canvas
     * @private
     */
    private stopVisualization(): void {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = undefined;
        }
        this.clearCanvas();
    }

    /**
     * Main method for the animation. It will call `redrawWaveform` if there new voic activity values in
     * `voicAcvitivyHistory`. If the activity already filled the canvas, it will shift the data to make room
     * for new activity values.
     * @private`
     */
    private animate(): void {
        if (!this.transcribing || !this.canvas || !this.canvasCtx) return;

        // Check if we have new volume data to add
        const hasNewData =
            this.voiceActivityHistory.length === 0 ||
            this.voiceActivityHistory[this.voiceActivityHistory.length - 1] !== this.currentVolume;

        if (hasNewData) {
            // Add new volume data to the end
            this.voiceActivityHistory.push(this.currentVolume);

            // Remove old data if we have too much
            if (this.voiceActivityHistory.length > this.maxHistory) {
                this.voiceActivityHistory.shift();
            }

            // Redraw the entire waveform
            this.redrawWaveform();
        }

        this.animationId = requestAnimationFrame(() => this.animate());
    }

    /**
     * Draws the background dots displayed when the canvas is emtpy.
     * @private
     */
    private drawBackgroundDots(): void {
        if (!this.canvas || !this.canvasCtx) return;

        const centerY = this.canvasHeight / 2;

        // Draw light gray dots across the entire canvas
        this.canvasCtx.fillStyle = '#ccc';

        for (let x = 0; x < this.canvasWidth; x += this.strokeWidth + this.strokeSpacing) {
            // Draw a small dot at the center line
            this.canvasCtx.fillRect(x, centerY - 0.5, this.strokeWidth, 1);
        }
    }

    /**
     * Draws the waveform with the current values in `voicAcvitivyHistory`
     * @private
     */
    private redrawWaveform(): void {
        if (!this.canvas || !this.canvasCtx) return;

        const centerY = this.canvasHeight / 2;
        const maxBarHeight = this.canvasHeight - 2;

        // Clear canvas with white background
        this.canvasCtx.fillStyle = '#ffffff';
        this.canvasCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

        // Draw background dots if the canvas is not full yet
        if (this.voiceActivityHistory.length < this.maxHistory) {
            this.drawBackgroundDots();
        }

        // Calculate the starting position (right-aligned)
        const totalWidth =
            this.voiceActivityHistory.length * (this.strokeWidth + this.strokeSpacing) - this.strokeSpacing;
        let startX = this.canvasWidth - totalWidth;

        // If we have more data than can fit, start from the left edge
        if (startX < 0) {
            startX = 0;
        }

        // Draw each stroke from left to right (oldest to newest)
        this.canvasCtx.strokeStyle = this.strokeColor;
        this.canvasCtx.lineWidth = this.strokeWidth;

        for (let i = 0; i < this.voiceActivityHistory.length; i += 1) {
            const x = startX + i * (this.strokeWidth + this.strokeSpacing);

            // Only draw if the stroke is within the canvas bounds
            if (x >= 0 && x + this.strokeWidth <= this.canvasWidth) {
                const volume = this.voiceActivityHistory[i];

                // Calculate stroke height based on the volume and the maxBarHeight
                let barHeight = Math.max(1, Math.round(volume * maxBarHeight));
                barHeight = Math.min(barHeight, maxBarHeight);

                // Draw the stroke
                this.canvasCtx.beginPath();
                this.canvasCtx.moveTo(x, centerY - barHeight / 2);
                this.canvasCtx.lineTo(x, centerY + barHeight / 2);
                this.canvasCtx.stroke();
            }
        }
    }

    /**
     * Clear the canvas with a white rectangle
     * @private
     */
    private clearCanvas(): void {
        if (this.canvas && this.canvasCtx) {
            // Clear with white background
            this.canvasCtx.fillStyle = '#ffffff';
            this.canvasCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

            // Draw background dots when canvas is empty
            this.drawBackgroundDots();
        }
        this.voiceActivityHistory = [];
    }

    /**
     * Cancel the transcription and don't trigger a service call by unsubscribing from the audio recording.
     */
    cancelTranscription(): void {
        if (this.audioRecordingSubscription) {
            this.audioRecordingSubscription.unsubscribe();
            this.audioRecordingSubscription = undefined;
        }
        if (this.transcribing) {
            this.audioRecordingService.stop();
        }
        this.transcriptionCancel.emit();
        this.transcribing = false;
    }

    /**
     * Stop the transcription. It will trigger a RECORDING_STOP event that will result in the audio being transcribed
     * by the service.
     */
    stopTranscription(): void {
        if (this.transcribing) {
            this.transcriptionStop.emit();
            this.audioRecordingService.stop();
        }
    }

    /**
     * Starts a transcription. Subscribes to AudioRecordingService.
     * @protected
     */
    async startTranscription(): Promise<void> {
        if (!this.transcribing) {
            this.settingUpTranscription = true;
            this.audioRecordingSubscription = this.audioRecordingService
                .getRecordingEvent()
                .subscribe((event: AudioRecordingEvent) => this.handlerNewTranscriptionServiceEvent(event));

            await this.audioRecordingService.start();
        }
    }

    // Handlers

    /**
     * Handles events from AudioRecordingService.
     * @param event
     */
    handlerNewTranscriptionServiceEvent(event: AudioRecordingEvent): void {
        switch (event.event) {
            case 'FREQUENCY_DATA':
                this.currentVolume = event.volume || 0;
                break;

            case 'RECORDING_STARTED':
                this.transcribing = true;
                this.settingUpTranscription = false;
                this.cdRef.detectChanges();
                this.startVisualization();
                break;

            case 'AUDIO_READY':
                this.processing = true;
                const base64Audio = event.audioData?.blob;
                if (base64Audio) {
                    const formData = new FormData();
                    formData.append('audio', base64Audio, 'audio.mp4');
                    this.ajaxService
                        .postDataObserveBody<AudioTranscription>(
                            this.urlsService.SYSTEMS.AI_AUDIO_TRANSCRIPTION,
                            formData
                        )
                        .subscribe({
                            next: (data) => {
                                this.inputText = data.transcription;
                                this.transcribedText.emit(this.inputText);
                                this.processing = false;
                                this.cdRef.detectChanges();
                            },
                            error: () => {
                                this.processing = false;
                            },
                        });
                }

                this.transcribing = false;
                this.stopVisualization();
                if (this.audioRecordingSubscription) {
                    this.audioRecordingSubscription.unsubscribe();
                    this.audioRecordingSubscription = undefined;
                }
                break;

            case 'RECORDING_STOPPED':
                break;

            case 'ERROR':
                this.transcriptionError.emit(event.error);
                this.transcribing = false;
                this.processing = false;
                this.settingUpTranscription = false;
                this.stopVisualization();
                if (this.audioRecordingSubscription) {
                    this.audioRecordingSubscription.unsubscribe();
                    this.audioRecordingSubscription = undefined;
                }
                this.cdRef.detectChanges();
                break;

            default:
                break;
        }
        this.cdRef.detectChanges();
    }

    protected onInputChange(event: Event): void {
        this.inputText = (event.target as HTMLInputElement).value;
        this.transcribedText.emit(this.inputText);
    }

    protected onEnterPressed(): void {
        if (this.inputText) {
            this.transcribedText.emit(this.inputText);
        }
    }
}
